/**
 * Display Mode Service
 * 
 * Centralized service for managing planet display mode preferences
 * Handles storage, retrieval, and synchronization across components
 */

import { devWarn } from '../../../../utils/devLog';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../utils/localStorageUtils';
import React from 'react';

export type PlanetDisplayMode = 'symbols' | 'short' | 'full';

export interface DisplayModeConfig {
  mode: PlanetDisplayMode;
}

/**
 * Storage key for global config
 */
const STORAGE_KEY = 'hora_planet_display_mode_global';

/**
 * Default configuration
 */
const DEFAULT_CONFIG: DisplayModeConfig = {
  mode: 'short' // Default to two-letter abbreviations from commit e80c574d
};

/**
 * Event emitter for display mode changes
 */
class DisplayModeEventEmitter {
  private listeners: Map<string, Function[]> = new Map();

  subscribe(event: string, callback: Function): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  emit(event: string, data?: any): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          devWarn('Error in display mode event callback:', error);
        }
      });
    }
  }
}

/**
 * Display Mode Service Class
 */
class DisplayModeServiceClass {
  private eventEmitter = new DisplayModeEventEmitter();
  private config: DisplayModeConfig;

  constructor() {
    this.config = this.loadConfig();
  }

  /**
   * Load configuration from localStorage
   */
  private loadConfig(): DisplayModeConfig {
    const saved = getLocalStorageItem<Partial<DisplayModeConfig> | null>(STORAGE_KEY, null);
    return saved ? { ...DEFAULT_CONFIG, ...saved } : { ...DEFAULT_CONFIG };
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    setLocalStorageItem(STORAGE_KEY, this.config);
  }

  /**
   * Get configuration (always global)
   */
  getConfig(_chartId?: string): DisplayModeConfig {
    return this.config;
  }

  /**
   * Update configuration (always global)
   */
  updateConfig(newConfig: Partial<DisplayModeConfig>, _chartId?: string): DisplayModeConfig {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();

    // Emit change event
    this.eventEmitter.emit('configChanged', {
      config: this.config,
      isGlobal: true
    });

    return this.config;
  }

  /**
   * Get current display mode for a chart
   */
  getDisplayMode(chartId?: string): PlanetDisplayMode {
    return this.getConfig(chartId).mode;
  }

  /**
   * Update display mode
   */
  setDisplayMode(mode: PlanetDisplayMode): void {
    this.updateConfig({ mode });
  }

  /**
   * Subscribe to configuration changes
   */
  onConfigChange(callback: (data: { config: DisplayModeConfig; isGlobal: boolean }) => void): () => void {
    return this.eventEmitter.subscribe('configChanged', callback);
  }

  /**
   * Reset to defaults
   */
  resetToDefaults(): void {
    this.config = { ...DEFAULT_CONFIG };
    this.saveConfig();

    this.eventEmitter.emit('configChanged', {
      config: this.config,
      isGlobal: true
    });
  }

  /**
   * Import configuration from external source
   */
  importConfig(config: DisplayModeConfig): void {
    this.updateConfig(config);
  }

  /**
   * Export configuration for backup/sharing
   */
  exportConfig(): DisplayModeConfig {
    return { ...this.config };
  }
}

/**
 * Singleton instance
 */
export const DisplayModeService = new DisplayModeServiceClass();

/**
 * React hook for using display mode service
 */
export const useDisplayModeService = (_chartId?: string) => {
  const [config, setConfig] = React.useState<DisplayModeConfig>(
    DisplayModeService.getConfig()
  );

  React.useEffect(() => {
    // Subscribe to changes (always global now)
    const unsubscribe = DisplayModeService.onConfigChange((data) => {
      setConfig(data.config);
    });

    return unsubscribe;
  }, []);

  const updateConfig = React.useCallback((newConfig: Partial<DisplayModeConfig>) => {
    DisplayModeService.updateConfig(newConfig);
  }, []);

  return {
    config,
    updateConfig,
    setDisplayMode: (mode: PlanetDisplayMode) => DisplayModeService.setDisplayMode(mode)
  };
};

// Make service available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).DisplayModeService = DisplayModeService;
}

export default DisplayModeService;